/**
 * Clamps a date range so `from` is not before `min` and `to` is not after `max`.
 * Dates are ISO strings (YYYY-MM-DD).
 *
 * @param {{ from: string, to: string }} range
 * @param {{ min: string, max: string }} bounds
 * @returns {{ from: string, to: string }}
 */
export function clampRange(range, { min, max }) {
  return {
    from: range.from < min ? min : range.from,
    to: range.to > max ? max : range.to
  }
}

/**
 * Formats a Date to a YYYY-MM-DD string using local-time fields.
 * @param {Date} date
 * @returns {string}
 */
export function toIsoDate(date) {
  const yyyy = date.getFullYear()
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Parses a YYYY-MM-DD string to a Date at local-time midnight.
 * @param {string} iso
 * @returns {Date}
 */
export function fromIsoDate(iso) {
  const [yyyy, mm, dd] = iso.split('-').map(Number)
  return new Date(yyyy, mm - 1, dd)
}

/**
 * Maps a preset key + a today-anchor (YYYY-MM-DD) to a date range.
 * Returns null for 'custom' (UI toggles to calendar mode).
 *
 * @param {'today'|'yesterday'|'last7days'|'last30days'|'thisMonth'|'lastMonth'|'thisYear'|'lastYear'|'custom'} preset
 * @param {string} today - YYYY-MM-DD anchor
 * @returns {{from: string, to: string} | null}
 */
export function presetToRange(preset, today) {
  const anchor = fromIsoDate(today)

  if (preset === 'today') {
    return { from: today, to: today }
  }
  if (preset === 'yesterday') {
    const y = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - 1)
    const iso = toIsoDate(y)
    return { from: iso, to: iso }
  }
  if (preset === 'last7days') {
    const from = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - 6)
    return { from: toIsoDate(from), to: today }
  }
  if (preset === 'last30days') {
    const from = new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - 29)
    return { from: toIsoDate(from), to: today }
  }
  if (preset === 'thisMonth') {
    const from = new Date(anchor.getFullYear(), anchor.getMonth(), 1)
    return { from: toIsoDate(from), to: today }
  }
  if (preset === 'lastMonth') {
    const from = new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1)
    const to = new Date(anchor.getFullYear(), anchor.getMonth(), 0)
    return { from: toIsoDate(from), to: toIsoDate(to) }
  }
  if (preset === 'thisYear') {
    const from = new Date(anchor.getFullYear(), 0, 1)
    return { from: toIsoDate(from), to: today }
  }
  if (preset === 'lastYear') {
    const y = anchor.getFullYear() - 1
    return { from: toIsoDate(new Date(y, 0, 1)), to: toIsoDate(new Date(y, 11, 31)) }
  }
  return null
}

/**
 * Ordered preset definitions. Single source of truth for preset keys + labels.
 * Imported by DateRangePresets.vue and formatRangeLabel below.
 */
export const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7days', label: 'Last 7 days' },
  { key: 'last30days', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'thisYear', label: 'This year' },
  { key: 'lastYear', label: 'Last year' },
  { key: 'custom', label: 'Custom' }
]

const EN_MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

/**
 * Returns the preset label if the range matches a preset for the given today-anchor,
 * otherwise a compact English date-range string.
 *
 * @param {{from: string, to: string}} range
 * @param {string} today - YYYY-MM-DD anchor
 * @returns {string}
 */
export function formatRangeLabel(range, today) {
  for (const { key, label } of PRESETS) {
    const preset = presetToRange(key, today)
    if (preset && preset.from === range.from && preset.to === range.to) {
      return label
    }
  }
  
  const from = fromIsoDate(range.from)
  const to = fromIsoDate(range.to)

  if (range.from === range.to) {
    return `${from.getDate()} ${EN_MONTHS_SHORT[from.getMonth()]} ${from.getFullYear()}`
  }

  if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
    return `${from.getDate()} – ${to.getDate()} ${EN_MONTHS_SHORT[from.getMonth()]} ${from.getFullYear()}`
  }

  if (from.getFullYear() === to.getFullYear()) {
    return `${from.getDate()} ${EN_MONTHS_SHORT[from.getMonth()]} – ${to.getDate()} ${EN_MONTHS_SHORT[to.getMonth()]} ${from.getFullYear()}`
  }

  return `${from.getDate()} ${EN_MONTHS_SHORT[from.getMonth()]} ${from.getFullYear()} – ${to.getDate()} ${EN_MONTHS_SHORT[to.getMonth()]} ${to.getFullYear()}`
}

/**
 * Returns the inclusive length of a date range expressed in months, using the
 * average Gregorian-year length (365.25 days) so the result is stable across
 * leap years. Used to annualise FTE-style metrics over arbitrary periods.
 *
 * @param {{from: string, to: string}} range
 * @returns {number}
 */
/**
 * Annotates a chart subtitle with the active range label.
 *
 * @param {string} base
 * @param {{from: string, to: string}} range
 * @param {string} today - YYYY-MM-DD anchor
 * @returns {string}
 */
export function annotateSubtitle(base, range, today) {
  return `${base} · ${formatRangeLabel(range, today)}`
}

export function monthsInRange(range) {
  const from = fromIsoDate(range.from)
  const to = fromIsoDate(range.to)
  const msPerDay = 1000 * 60 * 60 * 24
  const days = Math.round((to - from) / msPerDay) + 1
  return (days / 365.25) * 12
}